import React from 'react';
import {
    Box, Typography, Paper, Grid, Chip, CircularProgress, Alert,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    LinearProgress, Tooltip,
} from '@mui/material';
import {
    VolForecastStatusLabels, VolForecastStatusColors,
    MarketRegimeTypeLabels, MarketRegimeTypeColors,
} from '../../stockTradingEnums';

const formatPercent = (val) => val != null ? `${Number(val).toFixed(2)}%` : '-';
const formatDate = (val) => val ? new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '-';

const SectionTitle = ({ children }) => (
    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '0.95rem', mb: 2, mt: 3, color: '#1e293b' }}>{children}</Typography>
);
const EmptyCard = ({ message }) => (
    <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderStyle: 'dashed', borderColor: '#e2e8f0' }}>
        <Typography color="text.secondary" fontSize="0.85rem">{message || 'No data available'}</Typography>
    </Paper>
);
const TabLoading = () => (
    <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>
);

// Model display names
const MODEL_LABELS = { GARCH: 'GARCH(1,1)', HAR_RV: 'HAR-RV', SEQUENCE_VOL: 'EWMA', ATTENTION_VOL: 'Attention', ENSEMBLE: 'Ensemble' };
const HORIZON_LABELS = { INTRADAY: 'Intraday', ONE_DAY: '1 Day', ONE_WEEK: '1 Week', TWO_WEEK: '2 Weeks', ONE_MONTH: '1 Month', THREE_MONTH: '3 Months' };
const HORIZON_ORDER = ['ONE_DAY', 'ONE_WEEK', 'TWO_WEEK', 'ONE_MONTH', 'THREE_MONTH', 'INTRADAY'];

// Color helpers
const getVolColor = (val) => {
    if (val == null) return '#94a3b8';
    if (val > 50) return '#ef4444';
    if (val > 30) return '#f59e0b';
    return '#22c55e';
};
const getCorrelationColor = (val) => {
    if (val == null) return '#94a3b8';
    const abs = Math.abs(val);
    if (abs > 0.7) return '#3b82f6';
    if (abs > 0.4) return '#8b5cf6';
    return '#94a3b8';
};

const ForecastsTab = ({ data, loading, error }) => {
    if (loading) return <TabLoading />;
    if (error) return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;
    if (!data) return <EmptyCard />;

    const {
        vol_forecasts = [],
        ensemble_results = [],
        realized_vol = [],
        implied_vol = [],
        regime_detections = [],
        liquidity_correlations = [],
    } = data;

    // Group vol forecasts by model type, pick latest per horizon
    const forecastsByModel = {};
    vol_forecasts.forEach(f => {
        const model = f.model_type || 'UNKNOWN';
        if (!forecastsByModel[model]) forecastsByModel[model] = {};
        const horizon = f.horizon || 'ONE_DAY';
        if (!forecastsByModel[model][horizon]) forecastsByModel[model][horizon] = f;
    });
    const modelKeys = Object.keys(forecastsByModel);
    const horizonKeys = HORIZON_ORDER.filter(h => modelKeys.some(m => forecastsByModel[m][h]));

    // Latest implied vol
    const latestIV = implied_vol[0];
    // Latest regime
    const latestRegime = regime_detections[0];

    return (
        <Box>
            {/* ===== Summary Cards Row ===== */}
            <Grid container spacing={2} sx={{ mb: 1 }}>
                {/* Implied Vol Card */}
                <Grid item xs={12} sm={6} md={3}>
                    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, height: '100%' }}>
                        <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem' }}>
                            Implied Volatility
                        </Typography>
                        <Typography variant="h4" fontWeight={700} sx={{ mt: 0.5, color: latestIV ? getVolColor(latestIV.iv_value) : '#94a3b8' }}>
                            {latestIV ? formatPercent(latestIV.iv_value) : '-'}
                        </Typography>
                        {latestIV?.iv_percentile != null && (
                            <Box sx={{ mt: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography variant="caption" color="text.secondary">Percentile</Typography>
                                    <Typography variant="caption" fontWeight={600}>{formatPercent(latestIV.iv_percentile)}</Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={Math.min(latestIV.iv_percentile, 100)}
                                    sx={{ height: 6, borderRadius: 3, bgcolor: '#f1f5f9',
                                        '& .MuiLinearProgress-bar': { bgcolor: latestIV.iv_percentile > 70 ? '#ef4444' : latestIV.iv_percentile > 40 ? '#f59e0b' : '#22c55e', borderRadius: 3 }
                                    }}
                                />
                            </Box>
                        )}
                        {latestIV?.fetch_date && <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>{formatDate(latestIV.fetch_date)}</Typography>}
                    </Paper>
                </Grid>

                {/* IV vs RV Spread Card */}
                <Grid item xs={12} sm={6} md={3}>
                    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, height: '100%' }}>
                        <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem' }}>
                            IV - RV Spread
                        </Typography>
                        {latestIV?.rv_value != null ? (
                            <>
                                <Typography variant="h4" fontWeight={700} sx={{ mt: 0.5, color: (latestIV.iv_value - latestIV.rv_value) > 0 ? '#ef4444' : '#22c55e' }}>
                                    {((latestIV.iv_value || 0) - (latestIV.rv_value || 0)) > 0 ? '+' : ''}{formatPercent((latestIV.iv_value || 0) - (latestIV.rv_value || 0))}
                                </Typography>
                                <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'space-between' }}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">IV</Typography>
                                        <Typography variant="body2" fontWeight={600}>{formatPercent(latestIV.iv_value)}</Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography variant="caption" color="text.secondary">vs</Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'right' }}>
                                        <Typography variant="caption" color="text.secondary">RV</Typography>
                                        <Typography variant="body2" fontWeight={600}>{formatPercent(latestIV.rv_value)}</Typography>
                                    </Box>
                                </Box>
                            </>
                        ) : (
                            <Typography variant="h4" fontWeight={700} sx={{ mt: 0.5, color: '#94a3b8' }}>-</Typography>
                        )}
                    </Paper>
                </Grid>

                {/* Market Regime Card */}
                <Grid item xs={12} sm={6} md={3}>
                    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, height: '100%' }}>
                        <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem' }}>
                            Market Regime
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                            {latestRegime ? (
                                <>
                                    <Chip
                                        label={MarketRegimeTypeLabels[latestRegime.detected_regime] || latestRegime.detected_regime}
                                        color={MarketRegimeTypeColors[latestRegime.detected_regime] || 'default'}
                                        size="medium"
                                        sx={{ fontWeight: 600, fontSize: '0.8rem' }}
                                    />
                                    {latestRegime.regime_probability != null && (
                                        <Box sx={{ mt: 1.5 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                <Typography variant="caption" color="text.secondary">Confidence</Typography>
                                                <Typography variant="caption" fontWeight={600}>{formatPercent(latestRegime.regime_probability * 100)}</Typography>
                                            </Box>
                                            <LinearProgress
                                                variant="determinate"
                                                value={Math.min(latestRegime.regime_probability * 100, 100)}
                                                sx={{ height: 6, borderRadius: 3, bgcolor: '#f1f5f9',
                                                    '& .MuiLinearProgress-bar': { bgcolor: '#3b82f6', borderRadius: 3 }
                                                }}
                                            />
                                        </Box>
                                    )}
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>{formatDate(latestRegime.detection_date)}</Typography>
                                </>
                            ) : <Typography variant="h5" fontWeight={700} color="text.secondary">-</Typography>}
                        </Box>
                    </Paper>
                </Grid>

                {/* Ensemble Forecast Card */}
                <Grid item xs={12} sm={6} md={3}>
                    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, height: '100%' }}>
                        <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem' }}>
                            Ensemble Forecast
                        </Typography>
                        {ensemble_results[0] ? (
                            <>
                                <Typography variant="h4" fontWeight={700} sx={{ mt: 0.5, color: getVolColor(ensemble_results[0].ensemble_forecast) }}>
                                    {formatPercent(ensemble_results[0].ensemble_forecast)}
                                </Typography>
                                <Chip
                                    label={ensemble_results[0].weight_method?.replace('_', ' ') || 'Equal'}
                                    size="small" variant="outlined"
                                    sx={{ mt: 1, fontSize: '0.7rem', height: 22, textTransform: 'capitalize' }}
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                    {formatDate(ensemble_results[0].forecast_date)}
                                </Typography>
                            </>
                        ) : <Typography variant="h4" fontWeight={700} sx={{ mt: 0.5, color: '#94a3b8' }}>-</Typography>}
                    </Paper>
                </Grid>
            </Grid>

            {/* ===== Volatility Forecasts Matrix ===== */}
            <SectionTitle>Volatility Forecasts</SectionTitle>
            {modelKeys.length === 0 ? <EmptyCard message="No volatility forecasts available" /> : (
                <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', mb: 3 }}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#64748b', borderBottom: '2px solid #e2e8f0' }}>Model</TableCell>
                                    {horizonKeys.map(h => (
                                        <TableCell key={h} align="center" sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#64748b', borderBottom: '2px solid #e2e8f0' }}>
                                            {HORIZON_LABELS[h] || h}
                                        </TableCell>
                                    ))}
                                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#64748b', borderBottom: '2px solid #e2e8f0' }}>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {modelKeys.map(model => (
                                    <TableRow key={model} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                        <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{MODEL_LABELS[model] || model}</TableCell>
                                        {horizonKeys.map(h => {
                                            const f = forecastsByModel[model][h];
                                            return (
                                                <TableCell key={h} align="center">
                                                    {f ? (
                                                        <Tooltip title={`Range: ${formatPercent(f.confidence_lower)} - ${formatPercent(f.confidence_upper)}`} arrow>
                                                            <Typography variant="body2" fontWeight={600} sx={{ color: getVolColor(f.forecast_value), cursor: 'default' }}>
                                                                {formatPercent(f.forecast_value)}
                                                            </Typography>
                                                        </Tooltip>
                                                    ) : <Typography variant="body2" color="text.secondary">-</Typography>}
                                                </TableCell>
                                            );
                                        })}
                                        <TableCell align="center">
                                            {(() => {
                                                const anyForecast = Object.values(forecastsByModel[model])[0];
                                                return anyForecast?.status ? (
                                                    <Chip
                                                        label={VolForecastStatusLabels[anyForecast.status] || anyForecast.status}
                                                        color={VolForecastStatusColors[anyForecast.status] || 'default'}
                                                        size="small" sx={{ fontSize: '0.7rem', height: 22 }}
                                                    />
                                                ) : '-';
                                            })()}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            {/* ===== Ensemble Results ===== */}
            {ensemble_results.length > 0 && (
                <>
                    <SectionTitle>Ensemble Results</SectionTitle>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                        {ensemble_results.map((row, idx) => (
                            <Paper key={idx} variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Typography variant="h5" fontWeight={700} sx={{ color: getVolColor(row.ensemble_forecast) }}>
                                            {formatPercent(row.ensemble_forecast)}
                                        </Typography>
                                        <Chip label={row.weight_method?.replace('_', ' ') || 'Equal'} size="small" variant="outlined"
                                            sx={{ textTransform: 'capitalize', fontSize: '0.7rem', height: 22 }} />
                                    </Box>
                                    <Typography variant="caption" color="text.secondary">{formatDate(row.forecast_date)}</Typography>
                                </Box>
                                {row.ai_recommendation && (
                                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, fontSize: '0.82rem', bgcolor: '#f8fafc', p: 1.5, borderRadius: 1, borderLeft: '3px solid #3b82f6' }}>
                                        {row.ai_recommendation}
                                    </Typography>
                                )}
                                {row.component_weights_json && (
                                    <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                                        {Object.entries(row.component_weights_json).map(([model, weight]) => (
                                            <Chip key={model} label={`${MODEL_LABELS[model] || model}: ${(weight * 100).toFixed(0)}%`}
                                                size="small" variant="outlined"
                                                sx={{ fontSize: '0.68rem', height: 22, bgcolor: '#f1f5f9' }} />
                                        ))}
                                    </Box>
                                )}
                            </Paper>
                        ))}
                    </Box>
                </>
            )}

            {/* ===== Realized Volatility ===== */}
            <SectionTitle>Realized Volatility</SectionTitle>
            {realized_vol.length === 0 ? <EmptyCard message="No realized volatility data" /> : (
                <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', mb: 3 }}>
                    <Grid container>
                        {realized_vol.map((row, idx) => (
                            <Grid item xs={6} sm={4} md={3} key={idx} sx={{ borderRight: '1px solid #f1f5f9', borderBottom: '1px solid #f1f5f9' }}>
                                <Box sx={{ p: 2, textAlign: 'center' }}>
                                    <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                                        {row.estimator_type || '-'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.62rem' }}>
                                        {row.window_days}d window
                                    </Typography>
                                    <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5, color: getVolColor(row.annualized_value) }}>
                                        {formatPercent(row.annualized_value)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">{formatDate(row.calculation_date)}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Paper>
            )}

            {/* ===== Implied Volatility History ===== */}
            {implied_vol.length > 1 && (
                <>
                    <SectionTitle>IV History</SectionTitle>
                    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', mb: 3 }}>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#64748b' }}>Date</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#64748b' }}>IV</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#64748b' }}>Percentile</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#64748b' }}>RV</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#64748b' }}>Spread</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {implied_vol.map((row, idx) => {
                                        const spread = row.iv_value != null && row.rv_value != null ? row.iv_value - row.rv_value : null;
                                        return (
                                            <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                                <TableCell sx={{ fontSize: '0.8rem' }}>{formatDate(row.fetch_date)}</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 600, color: getVolColor(row.iv_value) }}>{formatPercent(row.iv_value)}</TableCell>
                                                <TableCell align="right">{row.iv_percentile != null ? formatPercent(row.iv_percentile) : '-'}</TableCell>
                                                <TableCell align="right" sx={{ color: getVolColor(row.rv_value) }}>{formatPercent(row.rv_value)}</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 600, color: spread != null ? (spread > 0 ? '#ef4444' : '#22c55e') : '#94a3b8' }}>
                                                    {spread != null ? `${spread > 0 ? '+' : ''}${formatPercent(spread)}` : '-'}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </>
            )}

            {/* ===== Regime Detections ===== */}
            {regime_detections.length > 0 && (
                <>
                    <SectionTitle>Regime History</SectionTitle>
                    <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
                        {regime_detections.map((row, idx) => (
                            <Paper key={idx} variant="outlined" sx={{ p: 2, borderRadius: 2, minWidth: 160, flex: '1 1 auto', maxWidth: 220,
                                borderLeft: idx === 0 ? '3px solid #3b82f6' : undefined }}>
                                <Chip
                                    label={MarketRegimeTypeLabels[row.detected_regime] || row.detected_regime}
                                    color={MarketRegimeTypeColors[row.detected_regime] || 'default'}
                                    size="small" sx={{ fontWeight: 600, fontSize: '0.72rem', mb: 1 }}
                                />
                                {row.regime_probability != null && (
                                    <Typography variant="body2" fontWeight={600} sx={{ color: '#475569' }}>
                                        {formatPercent(row.regime_probability * 100)} confidence
                                    </Typography>
                                )}
                                <Typography variant="caption" color="text.secondary">{formatDate(row.detection_date)}</Typography>
                                {idx === 0 && <Chip label="Current" size="small" sx={{ ml: 0.5, fontSize: '0.6rem', height: 18, bgcolor: '#dbeafe', color: '#2563eb' }} />}
                            </Paper>
                        ))}
                    </Box>
                </>
            )}

            {/* ===== Liquidity Correlations ===== */}
            {liquidity_correlations.length > 0 && (
                <>
                    <SectionTitle>Liquidity Correlations</SectionTitle>
                    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', mb: 3 }}>
                        <Grid container>
                            {liquidity_correlations.map((row, idx) => (
                                <Grid item xs={6} sm={4} md={3} key={idx} sx={{ borderRight: '1px solid #f1f5f9', borderBottom: '1px solid #f1f5f9' }}>
                                    <Box sx={{ p: 2, textAlign: 'center' }}>
                                        <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                                            {row.window_days}d Window
                                        </Typography>
                                        <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5, color: getCorrelationColor(row.correlation_coefficient) }}>
                                            {row.correlation_coefficient != null ? row.correlation_coefficient.toFixed(3) : '-'}
                                        </Typography>
                                        {row.correlation_strength != null && (
                                            <LinearProgress
                                                variant="determinate"
                                                value={Math.min(row.correlation_strength * 100, 100)}
                                                sx={{ height: 4, borderRadius: 2, mt: 1, bgcolor: '#f1f5f9',
                                                    '& .MuiLinearProgress-bar': { bgcolor: getCorrelationColor(row.correlation_coefficient), borderRadius: 2 }
                                                }}
                                            />
                                        )}
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                            {formatDate(row.calculation_date)}
                                        </Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </Paper>
                </>
            )}
        </Box>
    );
};

export default ForecastsTab;
